
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  HiOutlineScale,
  HiOutlineArrowDown,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineBriefcase,
  HiOutlineShieldCheck,
} from "react-icons/hi2";

import {
  Header,
  Fotter,
} from "../components/layout";

import {
  Loader,
  ServiceCard,
} from "../components/utils";

import { getAllServices } from "../redux/actions/services";
import { servicesPageImage } from "../assets";

/* ============================================
   CONFIGURATION

   The existing API starts with page 0.
============================================ */

const PAGE_SIZE = 9;

/* ============================================
   PAGINATION RANGE
============================================ */

function getPageRange(currentPage, totalPages) {
  const visibleCount = Math.min(totalPages, 5);

  const start = Math.max(
    0,
    Math.min(
      currentPage - 2,
      totalPages - visibleCount
    )
  );

  return Array.from(
    { length: visibleCount },
    (_, index) => start + index
  );
}

/* ============================================
   ALL SERVICES PAGE
============================================ */

function AllServiceses() {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();

  const [page, setPage] = useState(0);

  const {
    isLoading,
    Allservices,
    pagination,
  } = useSelector(
    (state) => state.Services || {}
  );

  /* ======================================
     LANGUAGE
     Direction is managed globally.
  ====================================== */

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  /* ======================================
     FETCH SERVICES
  ====================================== */

  useEffect(() => {
    dispatch(getAllServices(page, PAGE_SIZE));
  }, [dispatch, page]);

  const services = Array.isArray(Allservices)
    ? Allservices
    : [];

  const totalPages = Math.max(
    1,
    Number(pagination?.totalPages) || 1
  );

  const pageNumbers = getPageRange(
    page,
    totalPages
  );

  /* ======================================
     PAGE CONTENT
  ====================================== */

  const content = {
    home: isEnglish
      ? "Home"
      : "الرئيسية",

    eyebrow: isEnglish
      ? "AL NOMAN LAW OFFICE"
      : "مؤسسة النعمان للمحاماة",

    title: t("Practices"),

    description: t(
      "We offer our extensive expertise in various legal fields, providing tailored solutions to meet your legal needs with the highest professionalism."
    ),

    heroLabel: isEnglish
      ? "Legal Representation & Advisory"
      : "التمثيل والاستشارات القانونية",

    explore: isEnglish
      ? "Explore Our Services"
      : "استكشف خدماتنا",

    practicesEyebrow: isEnglish
      ? "OUR PRACTICE AREAS"
      : "مجالات الممارسة",

    practicesTitle: isEnglish
      ? "Legal Services Tailored to Your Needs"
      : "خدمات قانونية تلبي احتياجاتك",

    practicesDescription: isEnglish
      ? "Explore our practice areas and learn more about the legal services available to individuals and organizations."
      : "تعرف على مجالات عمل المؤسسة والخدمات القانونية المقدمة للأفراد والشركات والجهات المختلفة.",

    sectionLabel: isEnglish
      ? "Available Practice Areas"
      : "مجالات الخدمة المتاحة",

    previous: isEnglish
      ? "Previous"
      : "السابق",

    next: isEnglish
      ? "Next"
      : "التالي",

    page: isEnglish
      ? "Page"
      : "صفحة",

    of: isEnglish
      ? "of"
      : "من",

    emptyTitle: isEnglish
      ? "No Services Available"
      : "لا توجد خدمات متاحة",

    emptyDescription: isEnglish
      ? "Legal services will appear here when they become available."
      : "ستظهر الخدمات القانونية هنا عند توفرها.",

    closing: isEnglish
      ? "Professional attention to every legal matter."
      : "نهتم بالتفاصيل القانونية لكل مسألة.",
  };

  /* ======================================
     PAGINATION HANDLER
  ====================================== */

  const changePage = (nextPage) => {
    if (
      nextPage < 0 ||
      nextPage >= totalPages ||
      nextPage === page ||
      isLoading
    ) {
      return;
    }

    setPage(nextPage);

    document
      .getElementById("services-list")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  /* ======================================
     RENDER
  ====================================== */

  return (
    <>
      <Header active={3} />

      <main
        className="
          min-h-screen
          overflow-hidden
          bg-skin-page
          text-skin-text
        "
      >

        {/* ======================================
            01 — PAGE HERO
        ====================================== */}

        <section
          className="
            relative isolate
            overflow-hidden
            bg-[#102536]
            text-white

            dark:bg-[#081927]
          "
        >
          {/* Background glow */}
          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute -end-40 -top-48
              h-[530px] w-[530px]
              rounded-full
              bg-[#668DA5]/10
              blur-[100px]
            "
          />

          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute -start-44 -bottom-48
              h-[500px] w-[500px]
              rounded-full
              bg-[#D7B77F]/[0.035]
              blur-[110px]
            "
          />

          <div className="container relative z-10">

            <div
              className="
                grid items-center
                gap-12
                py-14

                md:py-16

                lg:min-h-[540px]
                lg:grid-cols-2
                lg:gap-16
                lg:py-20

                xl:gap-24
              "
            >

              {/* ==================================
                  HERO TEXT
              ================================== */}

              <div className="min-w-0">

                {/* Eyebrow */}
                <div className="mb-7 flex items-center gap-3">

                  <span
                    aria-hidden="true"
                    className="
                      h-px w-10
                      shrink-0
                      bg-[#D7B77F]
                    "
                  />

                  <span
                    className="
                      eyebrow
                      text-[#D7B77F]
                    "
                  >
                    {content.eyebrow}
                  </span>

                </div>

                {/* Main title */}
                <h1
                  className="
                    h1 max-w-[740px]
                    text-white
                  "
                >
                  {content.title}
                </h1>

                {/* Divider */}
                <div
                  aria-hidden="true"
                  className="
                    mt-7
                    h-[2px] w-16
                    rounded-full
                    bg-[#D7B77F]
                  "
                />

                {/* Description */}
                <p
                  className="
                    body-1
                    mt-7
                    max-w-[620px]
                    text-[#C3D2DD]
                  "
                >
                  {content.description}
                </p>

                {/* Hero action */}
                <div className="mt-10 flex">

                  <a
                    href="#services-list"
                    className="
                      group inline-flex
                      min-h-[54px]
                      items-center justify-center
                      gap-4

                      rounded-xl
                      border border-white/20
                      bg-white/[0.09]

                      px-6 py-3
                      text-white

                      backdrop-blur-xl

                      transition-all duration-300

                      hover:border-white/40
                      hover:bg-white/[0.16]

                      focus-visible:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-[#D7B77F]
                    "
                  >
                    <span className="button-sm">
                      {content.explore}
                    </span>

                    <span
                      className="
                        flex h-8 w-8
                        items-center justify-center

                        rounded-lg
                        bg-white/10

                        transition-transform
                        duration-300

                        group-hover:translate-y-0.5
                      "
                    >
                      <HiOutlineArrowDown
                        size={19}
                        aria-hidden="true"
                      />
                    </span>
                  </a>

                </div>

                {/* Subtle detail */}
                <div
                  className="
                    mt-11 flex items-center
                    gap-4

                    border-t border-white/10
                    pt-6
                  "
                >
                  <HiOutlineShieldCheck
                    size={24}
                    className="
                      shrink-0
                      text-[#D7B77F]
                    "
                    aria-hidden="true"
                  />

                  <span
                    className="
                      body-3
                      text-white/65
                    "
                  >
                    {content.closing}
                  </span>
                </div>

              </div>

              {/* ==================================
                  HERO IMAGE
              ================================== */}

              <div
                className="
                  relative min-w-0
                  pb-6
                "
              >

                {/* Decorative outline */}
                <div
                  aria-hidden="true"
                  className="
                    pointer-events-none
                    absolute -end-4 -top-4

                    h-32 w-32

                    border-e
                    border-t
                    border-[#D7B77F]/65

                    sm:-end-5
                    sm:-top-5
                  "
                />

                {/* Image container */}
                <div
                  className="
                    group relative
                    overflow-hidden

                    rounded-[22px]

                    border border-white/15
                    bg-[#18364A]

                    shadow-[0_25px_65px_rgba(0,0,0,0.20)]
                  "
                >
                  <img
                    src={servicesPageImage}
                    alt={content.title}
                    fetchPriority="high"
                    decoding="async"
                    className="
                      aspect-[16/11]
                      w-full
                      object-cover
                      object-center

                      transition-transform
                      duration-700

                      group-hover:scale-[1.035]

                      lg:aspect-[5/4]
                    "
                  />

                  {/* Image gradient */}
                  <div
                    aria-hidden="true"
                    className="
                      pointer-events-none
                      absolute inset-0

                      bg-gradient-to-t
                      from-[#061522]/70
                      via-transparent
                      to-transparent
                    "
                  />

                  {/* Corner icon */}
                  <div
                    className="
                      absolute start-5 top-5

                      flex h-12 w-12
                      items-center justify-center

                      rounded-xl
                      border border-white/20
                      bg-[#102536]/65

                      text-[#D7B77F]
                      backdrop-blur-lg
                    "
                  >
                    <HiOutlineScale
                      size={25}
                      aria-hidden="true"
                    />
                  </div>

                </div>

                {/* ==================================
                    FLOATING IMAGE CAPTION
                ================================== */}

                <div
                  className="
                    relative
                    -mt-10
                    ms-auto
                    me-4
                    flex max-w-[85%]
                    items-center gap-4

                    rounded-xl

                    border border-white/15
                    bg-[#1D3B50]

                    px-5 py-4

                    text-white

                    shadow-[0_16px_40px_rgba(0,0,0,0.16)]

                    sm:me-6
                    sm:max-w-[75%]
                  "
                >
                  <span
                    className="
                      flex h-10 w-10
                      shrink-0 items-center
                      justify-center

                      rounded-lg
                      bg-white/[0.08]

                      text-[#D7B77F]
                    "
                  >
                    <HiOutlineBriefcase
                      size={22}
                      aria-hidden="true"
                    />
                  </span>

                  <span
                    className="
                      body-3 font-semibold
                      text-white/90
                    "
                  >
                    {content.heroLabel}
                  </span>
                </div>

              </div>

            </div>
          </div>
        </section>

        {/* ======================================
            02 — SERVICES LIST
        ====================================== */}

        <section
          id="services-list"
          aria-labelledby="services-heading"
          className="
            relative
            scroll-mt-[90px]

            border-t border-[#D7E2EA]

            bg-[#EDF3F7]

            py-16
            sm:py-20
            lg:py-24

            dark:border-white/10
            dark:bg-[#13293B]

            transition-colors duration-300
          "
        >
          {/* Decorative background */}
          <div
            aria-hidden="true"
            className="
              pointer-events-none

              absolute -end-40 -top-40
              h-[420px] w-[420px]

              rounded-full
              bg-white/35
              blur-3xl

              dark:bg-[#294961]/15
            "
          />

          <div className="container relative">

            {/* ==================================
                SECTION HEADING
            ================================== */}

            <div
              className="
                mb-12
                flex flex-col
                justify-between gap-8

                lg:mb-14
                lg:flex-row
                lg:items-end
              "
            >
              <div className="max-w-[770px]">

                {/* Eyebrow */}
                <div className="mb-5 flex items-center gap-3">

                  <span
                    aria-hidden="true"
                    className="
                      h-px w-9
                      bg-skin-accent
                    "
                  />

                  <span
                    className="
                      eyebrow
                      text-skin-accent
                    "
                  >
                    {content.practicesEyebrow}
                  </span>

                </div>

                {/* Title */}
                <h2
                  id="services-heading"
                  className="
                    section-title
                    text-skin-heading
                  "
                >
                  {content.practicesTitle}
                </h2>

                {/* Description */}
                <p
                  className="
                    body-2
                    mt-5 max-w-[680px]
                    text-skin-muted
                  "
                >
                  {content.practicesDescription}
                </p>

              </div>

              {/* Small section identity */}
              <div
                className="
                  hidden shrink-0
                  items-center gap-3

                  lg:flex
                "
              >
                <span
                  className="
                    font-cairo
                    text-sm font-medium
                    text-skin-muted
                  "
                >
                  {content.sectionLabel}
                </span>

                <div
                  className="
                    flex h-12 w-12
                    items-center justify-center

                    rounded-xl

                    border border-skin-border
                    bg-skin-surface

                    text-skin-accent
                  "
                >
                  <HiOutlineBriefcase
                    size={23}
                    aria-hidden="true"
                  />
                </div>
              </div>

            </div>

            {/* ==================================
                LOADING STATE
            ================================== */}

            {isLoading ? (
              <div
                className="
                  flex min-h-[420px]
                  items-center justify-center
                "
              >
                <Loader />
              </div>
            ) : services.length > 0 ? (
              <>

                {/* ==================================
                    SERVICES CARDS
                ================================== */}

                <div
                  className="
                    grid grid-cols-1
                    items-stretch
                    gap-6

                    md:grid-cols-2
                    lg:grid-cols-3

                    lg:gap-7
                  "
                >
                  {services.map((service, index) => (
                    <ServiceCard
                      key={
                        service._id ||
                        service.id ||
                        index
                      }
                      service={service}
                      index={
                        page * PAGE_SIZE + index
                      }
                    />
                  ))}
                </div>

                {/* ==================================
                    PAGINATION
                ================================== */}

                {totalPages > 1 && (
                  <nav
                    aria-label={
                      isEnglish
                        ? "Services pagination"
                        : "ترقيم صفحات الخدمات"
                    }
                    className="
                      mt-14 flex
                      flex-col items-center
                      gap-6

                      border-t border-skin-border
                      pt-9

                      sm:flex-row
                      sm:justify-between

                      lg:mt-16
                    "
                  >
                    {/* Page counter */}
                    <div
                      className="
                        flex items-center gap-2

                        font-cairo
                        text-sm
                        tabular-nums
                      "
                    >
                      <span className="text-skin-muted">
                        {content.page}
                      </span>

                      <span className="font-bold text-skin-heading">
                        {String(page + 1).padStart(2, "0")}
                      </span>

                      <span className="text-skin-muted">
                        {content.of}
                      </span>

                      <span className="text-skin-muted">
                        {String(totalPages).padStart(2, "0")}
                      </span>
                    </div>

                    {/* Page navigation */}
                    <div
                      className="
                        flex flex-wrap
                        items-center justify-center
                        gap-2
                      "
                    >
                      {/* Previous */}
                      <button
                        type="button"
                        disabled={page === 0}
                        onClick={() =>
                          changePage(page - 1)
                        }
                        aria-label={content.previous}
                        className="
                          flex h-11 w-11
                          items-center justify-center

                          rounded-xl
                          border border-skin-border
                          bg-skin-surface

                          text-skin-heading

                          transition-all duration-200

                          hover:border-skin-accent/40
                          hover:bg-skin-soft

                          disabled:cursor-not-allowed
                          disabled:opacity-35
                          disabled:hover:bg-skin-surface

                          focus-visible:outline-none
                          focus-visible:ring-2
                          focus-visible:ring-skin-accent
                        "
                      >
                        <HiOutlineChevronLeft
                          size={20}
                          aria-hidden="true"
                          className="directional-arrow"
                        />
                      </button>

                      {/* Page numbers */}
                      {pageNumbers.map((number) => {
                        const active =
                          number === page;

                        return (
                          <button
                            key={number}
                            type="button"
                            onClick={() =>
                              changePage(number)
                            }
                            aria-label={`${content.page} ${
                              number + 1
                            }`}
                            aria-current={
                              active
                                ? "page"
                                : undefined
                            }
                            className={`
                              flex h-11 min-w-[44px]
                              items-center justify-center

                              rounded-xl
                              border px-3

                              font-cairo
                              text-sm font-semibold
                              tabular-nums

                              transition-all duration-200

                              focus-visible:outline-none
                              focus-visible:ring-2
                              focus-visible:ring-skin-accent

                              ${
                                active
                                  ? `
                                    border-[#19344D]
                                    bg-[#19344D]
                                    text-white
                                    shadow-soft

                                    dark:border-white/20
                                    dark:bg-white/15
                                  `
                                  : `
                                    border-skin-border
                                    bg-skin-surface
                                    text-skin-muted

                                    hover:border-skin-accent/35
                                    hover:bg-skin-soft
                                    hover:text-skin-heading
                                  `
                              }
                            `}
                          >
                            {String(number + 1).padStart(
                              2,
                              "0"
                            )}
                          </button>
                        );
                      })}

                      {/* Next */}
                      <button
                        type="button"
                        disabled={
                          page >= totalPages - 1
                        }
                        onClick={() =>
                          changePage(page + 1)
                        }
                        aria-label={content.next}
                        className="
                          flex h-11 w-11
                          items-center justify-center

                          rounded-xl
                          border border-skin-border
                          bg-skin-surface

                          text-skin-heading

                          transition-all duration-200

                          hover:border-skin-accent/40
                          hover:bg-skin-soft

                          disabled:cursor-not-allowed
                          disabled:opacity-35
                          disabled:hover:bg-skin-surface

                          focus-visible:outline-none
                          focus-visible:ring-2
                          focus-visible:ring-skin-accent
                        "
                      >
                        <HiOutlineChevronRight
                          size={20}
                          aria-hidden="true"
                          className="directional-arrow"
                        />
                      </button>
                    </div>

                  </nav>
                )}

              </>
            ) : (
              /* ==================================
                  EMPTY STATE
              ================================== */

              <div
                className="
                  flex min-h-[350px]
                  flex-col items-center
                  justify-center

                  rounded-[24px]

                  border border-skin-border
                  bg-skin-surface

                  px-6 py-12
                  text-center
                "
              >
                <div
                  className="
                    mb-7
                    flex h-20 w-20
                    items-center justify-center

                    rounded-2xl
                    bg-skin-soft

                    text-skin-accent
                  "
                >
                  <HiOutlineScale
                    size={36}
                    aria-hidden="true"
                  />
                </div>

                <h3 className="h5 text-skin-heading">
                  {content.emptyTitle}
                </h3>

                <p
                  className="
                    body-2
                    mt-4 max-w-[470px]
                    text-skin-muted
                  "
                >
                  {content.emptyDescription}
                </p>

              </div>
            )}

          </div>
        </section>

      </main>

      <Fotter />
    </>
  );
}

export default AllServiceses;
